/**
 * Which build a machine is running, and whether that is the current one.
 *
 *   owner, 2026-08-20: 把每一台機器上的 avengents cli 的 hash 放到 Machine List
 *   … 並且告知是否過期. 並且提供一個按鈕讓使用者從那裡就可以直接啟動更新.
 *
 * Paid for the same day: a machine ran 15 commits behind, its "cannot find
 * claude" was a bug fixed a day earlier, and NOTHING on any screen could have
 * said so — the version only existed in a DynamoDB row read by hand. The reason
 * it could not catch up on its own is why `update()` takes a channel: its baked
 * update URL pointed at a host that had stopped answering, so its auto-updater
 * had been failing silently for its whole life.
 *
 * THREE STATES, NEVER TWO. "Behind" and "cannot tell" are different facts and
 * only one of them is actionable; collapsing them would put a red badge on every
 * machine too old to report a version — which is precisely the population whose
 * owner most needs to trust the badge.
 */
import { relayRequest } from '../transport/relay'

/** Where the fleet's builds are published — same origin, so no CORS and no config. */
const CHANNEL = '/avengents-update.json'

export type VersionState = 'current' | 'stale' | 'unknown'

/**
 * Compare what a machine reports against what the channel serves.
 *
 * Both sides must be present to say anything: an empty machine version means an
 * edge too old to report one, and an unreachable channel means WE are the ones
 * who cannot tell. Either way the answer is `unknown` — the badge says "not
 * known", never "out of date", because a wrong red badge teaches people to
 * ignore the right ones.
 */
export function versionState(machine: string | undefined, channel: string | null): VersionState {
  if (!machine || !channel) return 'unknown'
  // A build from an uncommitted tree is stamped `<sha>-dirty`; it is not the
  // channel's build and saying "current" about it would be false.
  return machine === channel ? 'current' : 'stale'
}

/** The version the channel is serving right now, or null when it cannot be read. */
export async function fetchChannelVersion(): Promise<string | null> {
  try {
    const res = await fetch(CHANNEL, { cache: 'no-store' })
    if (!res.ok) return null
    const body = (await res.json()) as { version?: unknown }
    return typeof body.version === 'string' && body.version ? body.version : null
  } catch {
    return null
  }
}

/**
 * Ask a machine to update itself.
 *
 * The channel is passed EXPLICITLY rather than left to the machine's baked
 * default: the machines that need this button most are exactly the ones whose
 * baked channel is wrong, and a button that could only retry the same dead URL
 * would be decoration.
 *
 * Returns when the machine has ACCEPTED the job, not when it has finished — it
 * installs and then restarts, so nothing it could say afterwards would reach us.
 * The proof is the version it reports next, which the list already watches.
 */
export async function requestUpdate(
  deviceId: string,
): Promise<{ started: boolean; busy: boolean; version: string }> {
  const url = new URL(CHANNEL, window.location.origin).toString()
  const answer = await relayRequest(
    'POST',
    `/edge/update?url=${encodeURIComponent(url)}`,
    null,
    undefined,
    deviceId,
  )
  const body = (answer.body ?? {}) as {
    ok?: boolean
    started?: boolean
    busy?: boolean
    version?: string
    detail?: string
    error?: string
  }
  if (answer.status >= 400 || body.ok === false) {
    throw new Error(body.detail || body.error || `the machine refused the update (${answer.status})`)
  }
  /*
   * `busy` IS AN ANSWER, not a variant of yes. The edge replies
   * `{ok:true, started:false, busy:true}` when an update is ALREADY running
   * (selfUpdateRoute.ts) — a 2xx, so this used to be reported as "Update started"
   * and the one state a person needs to see while debugging a stuck update is the
   * one it hid. Kept separate from `started` rather than inferred from it, so the
   * caller cannot accidentally collapse them again.
   */
  return {
    started: body.started !== false,
    busy: body.busy === true,
    version: String(body.version ?? ''),
  }
}
